'use client';

// Polyfills for Array methods
import 'core-js/features/array/to-reversed';
import 'core-js/features/array/to-sorted';
import 'core-js/features/array/to-spliced';

// Polyfill for URL.parse
import 'core-js/features/url/parse';

// Polyfill for Promise.withResolvers
import 'core-js/features/promise/with-resolvers';

import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';

import { SnackbarProvider } from 'notistack';

import { SWRConfig } from 'swr';

import { Analytics } from '@vercel/analytics/react';

import type { ReactNode } from 'react';
import { use, useEffect } from 'react';
import posthog from 'posthog-js';

import { LicenseInfo } from '@mui/x-license';

import { enGB } from 'date-fns/locale';

import { env } from '@/env';
import { CurrentUserContext } from '@/context/CurrentUserContext';
import { Or2AnalyticsProvider } from '@/context/Or2AnalyticsContext';
import { SerwistUpdatePrompt } from '@/components/ServiceWorker/SerwistUpdatePrompt';

import { AssistantProvider } from '@/modules/assistant/context/AssistantContext';
import { AssistantUrlParamsHandler } from '@/modules/assistant/components/AssistantUrlParamsHandler';

import { ThemeRegistry } from '../ThemeRegistry/ThemeRegistry';
import type { GetConstructorArgs } from '../Apollo/ApolloWrapper';
import { ApolloWrapper, authedFetch } from '../Apollo/ApolloWrapper';
import { Or2HistoryContextProvider } from '../History/Or2HistoryContext';

import { FCMProvider } from './FCMProvider';

interface Props {
  children: ReactNode;
  apolloDelay?: number;
}

export function ClientLayout({ children, apolloDelay }: Props) {
  const { currentUserId } = use(CurrentUserContext);

  useEffect(() => {
    if (
      !env.NEXT_PUBLIC_POSTHOG_KEY ||
      env.NEXT_PUBLIC_DEV_VM ||
      !currentUserId
    ) {
      return;
    }

    posthog.identify(currentUserId);
  }, [currentUserId]);

  LicenseInfo.setLicenseKey(env.NEXT_PUBLIC_MUI_X_LICENSE_KEY);

  return (
    <>
      <Analytics
        beforeSend={(event) => {
          if (event.url.includes('/news')) {
            return null;
          }
          return event;
        }}
      />
      <ApolloWrapper delay={apolloDelay || 0}>
        <SWRConfig
          value={{
            fetcher: (...args: GetConstructorArgs<typeof Request>) =>
              authedFetch(...args).then((res) => res.json()),
          }}
        >
          <Or2AnalyticsProvider>
            <Or2HistoryContextProvider>
              <ThemeRegistry>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={enGB}
                >
                  <SnackbarProvider>
                    <SerwistUpdatePrompt />
                    <AssistantProvider>
                      <AssistantUrlParamsHandler />
                      <FCMProvider>{children}</FCMProvider>
                    </AssistantProvider>
                  </SnackbarProvider>
                </LocalizationProvider>
              </ThemeRegistry>
            </Or2HistoryContextProvider>
          </Or2AnalyticsProvider>
        </SWRConfig>
      </ApolloWrapper>
    </>
  );
}
